import { useState, useMemo, useCallback } from 'react';
import { useQuery } from 'data-fetching-client';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedDateTime from 'I18n/components/FormattedDateTime';
import SanitizedText from 'sanitize-text/components/SanitizedText';
import styled from 'styled-components';
import H5 from 'UIComponents/elements/headings/H5';
import Small from 'UIComponents/elements/Small';
import UIFlex from 'UIComponents/layout/UIFlex';
import UIBadge from 'UIComponents/badge/UIBadge';
import UIPaginator from 'UIComponents/paginator/UIPaginator';
import UIDropdownDivider from 'UIComponents/dropdown/UIDropdownDivider';
import * as RequestStates from 'marketplace-ui-common/requestStates/RequestState';
// @ts-expect-error untyped dependency
import LoadingWrapper from 'marketplace-ui-common/layout/components/LoadingWrapper';
import { GET_VERSION_HISTORY } from '../queries/versionHistoryQuery';
import VersionHistoryResponse from '../records/VersionHistoryResponse';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const HeaderRow = styled(UIFlex).withConfig({
  displayName: "VersionHistory__HeaderRow",
  componentId: "sc-1kyvprm-0"
})(["&&{width:100%;}"]);
const ActionsWrapper = styled.div.withConfig({
  displayName: "VersionHistory__ActionsWrapper",
  componentId: "sc-1kyvprm-1"
})(["margin-left:auto;"]);
const EMPTY_VERSION_HISTORY = new VersionHistoryResponse();
const MAX_VERSIONS_PER_PAGE = 20;
function Version({
  renderVersionActions,
  showDivider = true,
  showInstalled = false,
  version
}) {
  return /*#__PURE__*/_jsxs(UIFlex, {
    direction: "column",
    className: "m-bottom-4",
    children: [/*#__PURE__*/_jsxs(HeaderRow, {
      className: "m-bottom-1",
      children: [/*#__PURE__*/_jsx(H5, {
        "aria-level": 5,
        className: "m-bottom-0 is--heading-4",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "marketplace-ui-assets-core.version.number",
          options: {
            versionNumber: version.versionNumber
          }
        })
      }), showInstalled && /*#__PURE__*/_jsx(UIBadge, {
        className: "m-left-2 m-top-1",
        use: "koala",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "marketplace-ui-assets-core.version.installed"
        })
      }), renderVersionActions && /*#__PURE__*/_jsx(ActionsWrapper, {
        children: renderVersionActions(version)
      })]
    }), /*#__PURE__*/_jsx(Small, {
      use: "help",
      className: "m-bottom-2",
      children: /*#__PURE__*/_jsx(FormattedDateTime, {
        value: version.publishedAt,
        format: "ll"
      })
    }), /*#__PURE__*/_jsx(SanitizedText, {
      body: version.releaseNotes
    }), showDivider && /*#__PURE__*/_jsx(UIDropdownDivider, {})]
  });
}
export default function VersionHistory({
  errorMessage,
  installedVersionNumber,
  numVersions,
  offeringId,
  onTrackInteraction,
  renderVersionActions,
  versionHistoryQuery = GET_VERSION_HISTORY
}) {
  const [page, setPage] = useState(1);
  const {
    offset,
    limit
  } = useMemo(() => ({
    offset: (page - 1) * MAX_VERSIONS_PER_PAGE,
    limit: numVersions && numVersions <= MAX_VERSIONS_PER_PAGE * page ? numVersions : MAX_VERSIONS_PER_PAGE
  }), [numVersions, page]);
  const {
    loading,
    error,
    data,
    previousData
  } = useQuery(versionHistoryQuery, {
    variables: {
      offeringId,
      offset,
      limit
    }
  });
  const versionHistory = useMemo(() => {
    const versionHistoryQueryData = data || previousData;
    if (!versionHistoryQueryData) {
      return EMPTY_VERSION_HISTORY;
    }
    const {
      versionHistory: versionHistoryData
    } = versionHistoryQueryData;
    return new VersionHistoryResponse(versionHistoryData);
  }, [data, previousData]);
  const versionHistoryRequestState = useMemo(() => {
    if (loading) {
      return RequestStates.PENDING;
    }
    if (error) {
      return RequestStates.FAILED;
    }
    if (!data && !previousData) {
      return RequestStates.UNINITIALIZED;
    }
    return RequestStates.SUCCEEDED;
  }, [data, error, loading, previousData]);
  const pageCount = Math.ceil((numVersions || versionHistory.total) / MAX_VERSIONS_PER_PAGE);
  const needsPagination = pageCount > 1;
  const handlePageChange = useCallback(newPage => {
    setPage(newPage);
    if (onTrackInteraction) {
      onTrackInteraction('change version history page', {
        page: newPage
      });
    }
  }, [onTrackInteraction]);
  return /*#__PURE__*/_jsxs(LoadingWrapper, {
    errorMessage: errorMessage,
    requestStatus: versionHistoryRequestState,
    showLoadingOnUninitialized: true,
    children: [versionHistory.versionHistory.map(version => /*#__PURE__*/_jsx(Version, {
      version: version,
      showDivider: version !== versionHistory.versionHistory[versionHistory.versionHistory.length - 1],
      showInstalled: version.versionNumber === installedVersionNumber,
      renderVersionActions: renderVersionActions
    }, version.versionNumber)), needsPagination && /*#__PURE__*/_jsx(UIPaginator, {
      onPageChange: ({
        target: {
          value
        }
      }) => handlePageChange(value),
      page: page,
      pageCount: pageCount,
      maxVisiblePageButtons: 5
    })]
  });
}